import React from "react";

import type { Column, RowData } from "@tanstack/react-table-v9";

import TableV2IconButton from "./TableV2IconButton";
import DropDown from "../../DropDown/DropDown";
import { getColumnLabel } from "../columnUtils";
import type { TableV2Features } from "../features";
import type { TableV2MenuItem } from "../types";

type MenuAction = TableV2MenuItem;

const buildActions = <TData extends RowData>(
  column: Column<TableV2Features, TData, unknown>,
  isResized: boolean,
  onFit?: () => void,
): MenuAction[] => {
  const actions: MenuAction[] = [];
  const sorted = column.getIsSorted();
  const pinned = column.getIsPinned();

  if (column.getCanSort()) {
    if (sorted !== "asc") {
      actions.push({
        key: "asc",
        label: "Sort ascending",
        icon: "fa-arrow-up",
        run: () => column.toggleSorting(false),
      });
    }
    if (sorted !== "desc") {
      actions.push({
        key: "desc",
        label: "Sort descending",
        icon: "fa-arrow-down",
        run: () => column.toggleSorting(true),
      });
    }
  }
  if (column.getCanPin()) {
    if (pinned !== "start") {
      actions.push({
        key: "pin-start",
        label: "Pin left",
        icon: "fa-thumbtack",
        run: () => column.pin("start"),
      });
    }
    if (pinned !== "end") {
      actions.push({
        key: "pin-end",
        label: "Pin right",
        icon: "fa-thumbtack",
        run: () => column.pin("end"),
      });
    }
    if (pinned) {
      actions.push({
        key: "unpin",
        label: "Unpin",
        icon: "fa-thumbtack-slash",
        run: () => column.pin(false),
      });
    }
  }
  if (column.getCanResize() && onFit) {
    actions.push({
      key: "fit",
      label: "Fit to content",
      icon: "fa-left-right",
      run: onFit,
    });
  }
  if (column.getCanResize() && isResized) {
    actions.push({
      key: "reset-size",
      label: "Reset width",
      icon: "fa-arrows-left-right",
      run: () => column.resetSize(),
    });
  }
  actions.push(...(column.columnDef.meta?.menuItems ?? []));
  if (column.getCanHide()) {
    actions.push({
      key: "hide",
      label: "Hide column",
      icon: "fa-eye-slash",
      run: () => column.toggleVisibility(false),
    });
  }
  return actions;
};

const ColumnHeaderMenu = <TData extends RowData>({
  column,
  isResized = false,
  onFit,
}: {
  column: Column<TableV2Features, TData, unknown>;
  onFit?: () => void;
  isResized?: boolean;
}) => {
  const actions = buildActions(column, isResized, onFit);
  if (actions.length === 0) return null;

  return (
    <DropDown direction="down">
      <DropDown.Toggle testId={`column-menu-${column.id}`}>
        <TableV2IconButton
          tooltip={false}
          icon="fa-solid fa-ellipsis-vertical"
          label={`${getColumnLabel(column)} column options`}
          onClick={(event) => event.stopPropagation()}
        />
      </DropDown.Toggle>
      <DropDown.Menu>
        {actions.map((action) => (
          <DropDown.Item
            key={action.key}
            value={action.key}
            onClick={action.run}
          >
            <i className={`fa-solid ${action.icon}`} style={{ width: 20 }} />
            {action.label}
          </DropDown.Item>
        ))}
      </DropDown.Menu>
    </DropDown>
  );
};

export default ColumnHeaderMenu;
